<template>
  <div class="modal fade" id="passFailModal" tabindex="-1" aria-labelledby="passFailModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="passFailModalLabel">
            Manual Evaluation
            <span v-if="student"> — UCO {{ student.uco }}<span v-if="student.name" class="text-muted fw-normal"> ({{ student.name }})</span></span>
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="saving" class="text-center my-4">
            <div class="spinner-border" role="status">
              <span class="visually-hidden">Saving...</span>
            </div>
          </div>
          <div v-else-if="subtasks && subtasks.length">
            <p class="text-muted small mb-3">Select Pass or Fail for each subtask. Optionally add a note. Only subtasks with a selection will be saved.</p>
            <div v-for="subtask in subtasks" :key="subtask.id" class="mb-3 border rounded p-3">
              <div class="d-flex align-items-center justify-content-between">
                <span class="fw-semibold">{{ subtask.title }}</span>
                <div class="btn-group" role="group" :aria-label="'Result for ' + subtask.title">
                  <button
                    type="button"
                    :class="['btn btn-sm', results[subtask.id] && results[subtask.id].result === true ? 'btn-success' : 'btn-outline-success']"
                    @click="setResult(subtask.id, true)"
                  >
                    <i class="bi bi-check-lg me-1"></i>Pass
                  </button>
                  <button
                    type="button"
                    :class="['btn btn-sm', results[subtask.id] && results[subtask.id].result === false ? 'btn-danger' : 'btn-outline-danger']"
                    @click="setResult(subtask.id, false)"
                  >
                    <i class="bi bi-x-lg me-1"></i>Fail
                  </button>
                </div>
              </div>
              <div v-if="results[subtask.id] && results[subtask.id].result !== null && results[subtask.id].result !== undefined" class="mt-2">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Note (optional)"
                  maxlength="2000"
                  v-model="results[subtask.id].note"
                />
              </div>
            </div>
          </div>
          <div v-else class="alert alert-info">No evaluable subtasks found for this homework.</div>
        </div>
        <div class="modal-footer">
          <div class="me-auto d-flex gap-2">
            <button type="button" class="btn btn-outline-success btn-sm" :disabled="saving" @click="bulkSet(true)">
              <i class="bi bi-check-all me-1"></i>Pass all
            </button>
            <button type="button" class="btn btn-outline-danger btn-sm" :disabled="saving" @click="bulkSet(false)">
              <i class="bi bi-x-circle me-1"></i>Fail all
            </button>
          </div>
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="saving || nothingSelected" @click="submit">
            Save
          </button>
        </div>
      </div>
    </div>
  </div>

  <StemErrorModal ref="stemErrorModal" />
</template>

<script>
import { Modal } from 'bootstrap';
import EvaluationController from '@/controllers/EvaluationController';
import StemErrorModal from '@/components/StemErrorModal.vue';

export default {
  name: 'PassFailModal',
  components: { StemErrorModal },
  props: {
    student: { type: Object, default: null },
    subtasks: { type: Array, default: () => [] },
    deadlineId: { type: Number, default: null },
    manualResults: { type: Object, default: () => ({}) },
  },
  emits: ['saved'],
  data() {
    return {
      results: {},
      saving: false,
    };
  },
  computed: {
    nothingSelected() {
      return !Object.values(this.results).some(r => r.result !== null && r.result !== undefined);
    },
  },
  watch: {
    subtasks: {
      immediate: true,
      handler(val) {
        this.results = this._buildResults(val);
      },
    },
    student() {
      // Reset and pre-populate when the target student changes
      this.results = this._buildResults(this.subtasks);
    },
  },
  methods: {
    _buildResults(subtasks) {
      const r = {};
      (subtasks || []).forEach(s => {
        const existing = this.manualResults[s.id];
        r[s.id] = {
          result: existing ? existing.result : null,
          note: existing ? (existing.note || '') : '',
        };
      });
      return r;
    },
    setResult(subtaskId, val) {
      this.results = {
        ...this.results,
        [subtaskId]: { result: val, note: this.results[subtaskId]?.note || '' },
      };
    },
    bulkSet(val) {
      const r = {};
      (this.subtasks || []).forEach(s => {
        r[s.id] = { result: val, note: this.results[s.id]?.note || '' };
      });
      this.results = r;
    },
    async submit() {
      if (!this.student || !this.deadlineId) return;
      this.saving = true;
      const toSave = Object.entries(this.results).filter(
        ([, v]) => v.result !== null && v.result !== undefined
      );
      const failed = [];
      for (const [subtaskId, { result, note }] of toSave) {
        try {
          await EvaluationController.setManualResult({
            student_uco: this.student.uco,
            deadline_id: this.deadlineId,
            subtask_id: parseInt(subtaskId),
            result,
            note: note || null,
          });
        } catch (error) {
          const subtask = (this.subtasks || []).find(s => s.id === parseInt(subtaskId));
          failed.push(subtask ? subtask.title : `subtask ${subtaskId}`);
        }
      }
      const saved = toSave.length - failed.length;
      if (saved > 0) {
        this.$emit('saved');
      }
      if (failed.length > 0) {
        this.$refs.stemErrorModal.show(
          'Error',
          `Could not save result for: ${failed.join(', ')}. ${saved > 0 ? `${saved} subtask(s) were saved.` : ''}`
        );
      } else {
        const el = document.getElementById('passFailModal');
        const inst = Modal.getInstance(el);
        if (inst) inst.hide();
      }
      this.saving = false;
    },
  },
};
</script>
